import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { API } from "../lib/api";
import { toast } from "sonner";

const fmt = (n) => `$${n.toFixed(2)} AUD`;

const COUNTRIES = [
    ["AU", "Australia"], ["NZ", "New Zealand"], ["US", "United States"],
    ["CA", "Canada"], ["GB", "United Kingdom"], ["IE", "Ireland"],
    ["SG", "Singapore"], ["HK", "Hong Kong"], ["JP", "Japan"],
    ["DE", "Germany"], ["FR", "France"], ["IT", "Italy"], ["ES", "Spain"],
    ["NL", "Netherlands"], ["SE", "Sweden"], ["NO", "Norway"],
    ["DK", "Denmark"], ["FI", "Finland"], ["CH", "Switzerland"],
    ["AT", "Austria"], ["BE", "Belgium"],
];

// Whitelist of the 8 individual bar slugs — must match backend ELIGIBLE_BAR_SLUGS.
// Used only for the client-side "preview" total when a discount code is entered;
// the server is authoritative for the actual charge.
const ELIGIBLE_BAR_SLUGS = new Set([
    "alati-signature-salt",
    "himalayan-rhubarb-unscented",
    "goat-milk-oatmeal-turmeric",
    "madder-cinnamon-charcoal",
    "himalayan-rhubarb-cinnamon-clove",
    "indigo-charcoal",
    "organic-cacao-tallow",
    "pure-tallow",
]);

const DISCOUNT_CODES = {
    COUSIN5: 5,
    YOUROCK10: 10,
    THANKYOU15: 15,
    MYSKINLOOKSSOYOUNG: 3,
    ILOVEMYSKIN: 4,
};
const DISCOUNT_CODE_MAX_BARS = { MYSKINLOOKSSOYOUNG: 25, ILOVEMYSKIN: 25 };
const DISCOUNT_CODE_LIMIT_MESSAGE = "This personal price code is limited to 25 soap bars per order.";

const Checkout = () => {
    const { items, totals, giftBox } = useCart();
    const [busy, setBusy] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState("ship");
    const [country, setCountry] = useState("AU");
    const [pickup, setPickup] = useState({ full_name: "", email: "", mobile: "", suburb: "", postcode: "" });
    const [ship, setShip] = useState({
        full_name: "", email: "", mobile: "",
        address_line1: "", address_line2: "",
        suburb: "", state: "", postcode: "",
    });
    const [codeInput, setCodeInput] = useState("");
    const [appliedCode, setAppliedCode] = useState(null); // uppercase key or null
    const [codeError, setCodeError] = useState("");

    const isPickup = deliveryMethod === "pickup";
    const effectiveCountry = isPickup ? "AU" : country;
    const isIntl = effectiveCountry !== "AU";

    // Count bars and detect slabs — for intl-quote gating (unchanged rules)
    const barCount = useMemo(
        () => items.reduce((n, i) => n + (i.bars_per_unit || 1) * i.quantity, 0),
        [items]
    );
    const hasSlab = useMemo(
        () => items.some((i) => (i.category === "artisan-slab") || /slab/i.test(i.slug || "")),
        [items]
    );
    const eligibleBarCount = useMemo(
        () => items.reduce((n, i) => n + (ELIGIBLE_BAR_SLUGS.has(i.slug) ? i.quantity : 0), 0),
        [items]
    );

    const requiresQuote = isIntl && (hasSlab || barCount >= 4);

    // Client-side preview totals. Server is authoritative for the actual charge.
    const preview = useMemo(() => {
        if (appliedCode) {
            const unitPrice = DISCOUNT_CODES[appliedCode];
            let subtotal = 0;
            for (const i of items) {
                const p = ELIGIBLE_BAR_SLUGS.has(i.slug) ? unitPrice : i.price;
                subtotal += p * i.quantity;
            }
            return {
                subtotal,
                bundleDiscount: 0,
                giftBoxFee: giftBox ? 8 : 0,
                shipping: 0,
                total: subtotal + (giftBox ? 8 : 0),
            };
        }
        // No code — use existing cart totals but adjust shipping by country/pickup
        const shipping = isPickup
            ? 0
            : requiresQuote
                ? 0
                : isIntl
                    ? 20
                    : totals.shipping;
        return {
            subtotal: totals.subtotal,
            bundleDiscount: totals.bundleDiscount,
            giftBoxFee: totals.giftBoxFee,
            shipping,
            total: totals.subtotal - totals.bundleDiscount + totals.giftBoxFee + shipping,
        };
    }, [items, appliedCode, giftBox, isPickup, isIntl, requiresQuote, totals]);

    const applyCode = async () => {
        const raw = (codeInput || "").trim().toUpperCase();
        setCodeError("");
        if (!raw) return;
        if (!(raw in DISCOUNT_CODES)) {
            setCodeError("Invalid discount code.");
            return;
        }
        if (effectiveCountry !== "AU") {
            setCodeError("These discount codes are for Australian orders only.");
            return;
        }
        if (eligibleBarCount === 0) {
            setCodeError("This code only applies to individual soap bars.");
            return;
        }
        const cap = DISCOUNT_CODE_MAX_BARS[raw];
        if (cap && eligibleBarCount > cap) {
            setCodeError(DISCOUNT_CODE_LIMIT_MESSAGE);
            return;
        }
        setAppliedCode(raw);
        setCodeInput(raw);
        toast.success(`Personal price applied — ${raw}`);
    };
    const removeCode = () => {
        setAppliedCode(null);
        setCodeInput("");
        setCodeError("");
    };

    // Pickup form validation — all 5 fields must be non-empty
    const pickupComplete = useMemo(() => {
        if (!isPickup) return true;
        return ["full_name", "email", "mobile", "suburb", "postcode"].every((k) => (pickup[k] || "").trim().length > 0);
    }, [isPickup, pickup]);

    // Ship form validation — all required delivery fields must be non-empty
    const shipComplete = useMemo(() => {
        if (isPickup) return true;
        if (requiresQuote) return true;  // quote path bypasses payment entirely
        return ["full_name", "email", "mobile", "address_line1", "suburb", "state", "postcode"]
            .every((k) => (ship[k] || "").trim().length > 0);
    }, [isPickup, requiresQuote, ship]);

    const canContinue = items.length > 0 && !requiresQuote && pickupComplete && shipComplete && !busy;

    const goToStripe = async () => {
        if (!canContinue) return;
        setBusy(true);
        try {
            const body = {
                items: items.map((i) => ({ product_slug: i.slug, quantity: i.quantity })),
                gift_box: giftBox,
                origin_url: window.location.origin,
                country: effectiveCountry,
                delivery_method: deliveryMethod,
            };
            if (isPickup) body.pickup_details = pickup;
            if (!isPickup) body.ship_details = { ...ship, country: effectiveCountry };
            if (appliedCode) body.discount_code = appliedCode;
            const res = await fetch(`${API}/checkout/session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Could not start checkout");
            }
            const data = await res.json();
            if (!data.url) throw new Error("No checkout URL received");
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.message || "Could not start checkout");
            setBusy(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="bg-[#FAF9F6] min-h-[70vh] px-6 lg:px-10 py-24" data-testid="checkout-empty">
                <div className="max-w-xl mx-auto text-center">
                    <h1 className="font-serif text-4xl">Your cart is quiet.</h1>
                    <p className="mt-5 text-[#4A4A46]">Add a bar before checking out.</p>
                    <Link to="/shop" className="btn-primary mt-10 inline-flex" data-testid="empty-shop-cta">
                        Shop Soap
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page" className="bg-[#FAF9F6]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
                <p className="label">Checkout</p>
                <h1 className="font-serif text-5xl mt-5">A quiet checkout.</h1>
                <p className="mt-3 text-sm text-[#737562]">
                    Continue to Stripe&rsquo;s secure checkout to enter your payment details.
                </p>
            </div>

            <div className="max-w-5xl mx-auto px-6 lg:px-10 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Order summary */}
                <aside className="lg:col-span-7 order-2 lg:order-1">
                    <div className="bg-[#F0EBE1] p-8">
                        <h2 className="font-serif text-2xl">Order summary</h2>
                        <div className="mt-6 space-y-5" data-testid="checkout-items">
                            {items.map((i) => (
                                <div key={i.slug} className="flex gap-4" data-testid={`checkout-item-${i.slug}`}>
                                    <div className="w-16 h-20 product-image-wrap shrink-0">
                                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-serif text-lg">{i.name}</p>
                                        <p className="text-sm text-[#4A4A46]">Qty {i.quantity}</p>
                                    </div>
                                    <p className="text-sm">
                                        {fmt((appliedCode && ELIGIBLE_BAR_SLUGS.has(i.slug) ? DISCOUNT_CODES[appliedCode] : i.price) * i.quantity)}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <hr className="divider my-6" />
                        <div className="text-sm space-y-2">
                            <div className="flex justify-between">
                                <span className="text-[#4A4A46]">Subtotal</span>
                                <span data-testid="checkout-subtotal">{fmt(preview.subtotal)}</span>
                            </div>
                            {appliedCode && (
                                <div className="flex justify-between text-[#6A7059]" data-testid="checkout-code-applied">
                                    <span>Personal price · {appliedCode}</span>
                                    <button type="button" onClick={removeCode} className="text-xs underline" data-testid="remove-code-button">
                                        remove
                                    </button>
                                </div>
                            )}
                            {!appliedCode && preview.bundleDiscount > 0 && (
                                <div className="flex justify-between text-[#6A7059]">
                                    <span>Bundle · {barCount >= 5 ? "5 bars, save 20%" : "3 bars, save 15%"}</span>
                                    <span>-{fmt(preview.bundleDiscount)}</span>
                                </div>
                            )}
                            {giftBox && (
                                <div className="flex justify-between">
                                    <span className="text-[#4A4A46]">Gift box</span>
                                    <span>{fmt(preview.giftBoxFee)}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-[#4A4A46]">{isPickup ? "Delivery" : "Shipping"}</span>
                                <span data-testid="checkout-shipping">
                                    {isPickup
                                        ? "Free Local Pickup — Darwin"
                                        : requiresQuote
                                            ? "Quoted separately"
                                            : preview.shipping === 0
                                                ? "Free"
                                                : fmt(preview.shipping)}
                                </span>
                            </div>
                            <div className="flex justify-between pt-3 border-t border-[#C7C0B3] text-base font-medium">
                                <span>Total</span>
                                <span data-testid="checkout-total">{requiresQuote ? "—" : fmt(preview.total)}</span>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Delivery + Stripe panel */}
                <div className="lg:col-span-5 order-1 lg:order-2">
                    <div className="border border-[#E3DED5] bg-white p-8">
                        <div className="flex items-center gap-2 text-[#6A7059]">
                            <Lock size={16} strokeWidth={1.4} />
                            <span className="text-xs uppercase tracking-[0.22em]">Secured by Stripe</span>
                        </div>
                        <h2 className="font-serif text-2xl mt-4 leading-snug">Continue to secure checkout.</h2>

                        {/* Delivery method */}
                        <fieldset className="mt-6" data-testid="delivery-method-block">
                            <legend className="label mb-3">Delivery method</legend>
                            <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid="delivery-method-ship-label">
                                <input
                                    type="radio"
                                    name="delivery_method"
                                    value="ship"
                                    checked={!isPickup}
                                    onChange={() => setDeliveryMethod("ship")}
                                    className="mt-1"
                                    data-testid="delivery-method-ship"
                                />
                                <span className="text-sm leading-relaxed">
                                    <strong>Ship to me</strong>
                                    <br />
                                    <span className="text-[#737562]">Australia A$9.50 (free over $80) · International A$20 (up to 3 bars).</span>
                                </span>
                            </label>
                            <label className="flex items-start gap-3 py-2 cursor-pointer" data-testid="delivery-method-pickup-label">
                                <input
                                    type="radio"
                                    name="delivery_method"
                                    value="pickup"
                                    checked={isPickup}
                                    onChange={() => setDeliveryMethod("pickup")}
                                    className="mt-1"
                                    data-testid="delivery-method-pickup"
                                />
                                <span className="text-sm leading-relaxed">
                                    <strong>Free Local Pickup — Darwin</strong>
                                    <br />
                                    <span className="text-[#737562]">ALATI will contact you by phone or email to arrange a time and provide the pickup location.</span>
                                </span>
                            </label>
                        </fieldset>

                        {/* Ship-only country selector + delivery details */}
                        {!isPickup && (
                            <>
                                <label className="block mt-4" data-testid="checkout-country-block">
                                    <span className="label mb-2 block">Delivery country</span>
                                    <select
                                        className="alati-input"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        required
                                        data-testid="checkout-country"
                                    >
                                        {COUNTRIES.map(([c, n]) => (
                                            <option key={c} value={c}>{n}</option>
                                        ))}
                                    </select>
                                    <p className="mt-2 text-[11px] text-[#737562] leading-relaxed" data-testid="intl-shipping-note">
                                        International shipping is A$20 for orders of up to three bars. Larger orders and soap slabs are quoted personally before payment. Duties, customs and local taxes are not included and may be payable by the recipient.
                                    </p>
                                </label>

                                {!requiresQuote && (
                                <div className="mt-6 space-y-3" data-testid="ship-fields">
                                    <p className="label">Delivery details</p>
                                    <p className="text-[11px] text-[#737562] leading-relaxed -mt-1">
                                        Please enter the full delivery address and a mobile number so we can dispatch your order and reach you if the courier needs to.
                                    </p>
                                    <label className="block">
                                        <span className="label mb-1 block">Full name</span>
                                        <input className="alati-input" value={ship.full_name} onChange={(e) => setShip({ ...ship, full_name: e.target.value })} data-testid="ship-full-name" autoComplete="name" />
                                    </label>
                                    <label className="block">
                                        <span className="label mb-1 block">Email</span>
                                        <input type="email" className="alati-input" value={ship.email} onChange={(e) => setShip({ ...ship, email: e.target.value })} data-testid="ship-email" autoComplete="email" />
                                    </label>
                                    <label className="block">
                                        <span className="label mb-1 block">Mobile / phone</span>
                                        <input type="tel" className="alati-input" value={ship.mobile} onChange={(e) => setShip({ ...ship, mobile: e.target.value })} data-testid="ship-mobile" autoComplete="tel" />
                                    </label>
                                    <label className="block">
                                        <span className="label mb-1 block">Street address</span>
                                        <input className="alati-input" value={ship.address_line1} onChange={(e) => setShip({ ...ship, address_line1: e.target.value })} data-testid="ship-line1" autoComplete="address-line1" />
                                    </label>
                                    <label className="block">
                                        <span className="label mb-1 block">Apartment / unit / floor (optional)</span>
                                        <input className="alati-input" value={ship.address_line2} onChange={(e) => setShip({ ...ship, address_line2: e.target.value })} data-testid="ship-line2" autoComplete="address-line2" />
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="label mb-1 block">Suburb / city</span>
                                            <input className="alati-input" value={ship.suburb} onChange={(e) => setShip({ ...ship, suburb: e.target.value })} data-testid="ship-suburb" autoComplete="address-level2" />
                                        </label>
                                        <label className="block">
                                            <span className="label mb-1 block">State / region</span>
                                            <input className="alati-input" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} data-testid="ship-state" autoComplete="address-level1" />
                                        </label>
                                    </div>
                                    <label className="block">
                                        <span className="label mb-1 block">Postcode</span>
                                        <input className="alati-input" value={ship.postcode} onChange={(e) => setShip({ ...ship, postcode: e.target.value })} data-testid="ship-postcode" autoComplete="postal-code" />
                                    </label>
                                </div>
                                )}
                            </>
                        )}

                        {/* Pickup contact fields */}
                        {isPickup && (
                            <div className="mt-4 space-y-3" data-testid="pickup-fields">
                                <label className="block">
                                    <span className="label mb-1 block">Full name</span>
                                    <input className="alati-input" value={pickup.full_name} onChange={(e) => setPickup({ ...pickup, full_name: e.target.value })} data-testid="pickup-full-name" />
                                </label>
                                <label className="block">
                                    <span className="label mb-1 block">Email</span>
                                    <input type="email" className="alati-input" value={pickup.email} onChange={(e) => setPickup({ ...pickup, email: e.target.value })} data-testid="pickup-email" />
                                </label>
                                <label className="block">
                                    <span className="label mb-1 block">Mobile</span>
                                    <input type="tel" className="alati-input" value={pickup.mobile} onChange={(e) => setPickup({ ...pickup, mobile: e.target.value })} data-testid="pickup-mobile" />
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="label mb-1 block">Suburb</span>
                                        <input className="alati-input" value={pickup.suburb} onChange={(e) => setPickup({ ...pickup, suburb: e.target.value })} data-testid="pickup-suburb" />
                                    </label>
                                    <label className="block">
                                        <span className="label mb-1 block">Postcode</span>
                                        <input className="alati-input" value={pickup.postcode} onChange={(e) => setPickup({ ...pickup, postcode: e.target.value })} data-testid="pickup-postcode" />
                                    </label>
                                </div>
                                <p className="text-[11px] text-[#737562] leading-relaxed" data-testid="pickup-privacy-note">
                                    Your details are shared with ALATI only. We&rsquo;ll contact you personally to arrange collection.
                                </p>
                            </div>
                        )}

                        {/* Discount code */}
                        {!requiresQuote && (
                            <div className="mt-6" data-testid="discount-code-block">
                                <label className="label mb-2 block">Personal price code (optional)</label>
                                <div className="flex gap-2">
                                    <input
                                        className="alati-input flex-1"
                                        placeholder="Enter code"
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value)}
                                        disabled={!!appliedCode}
                                        data-testid="discount-code-input"
                                    />
                                    {!appliedCode ? (
                                        <button type="button" onClick={applyCode} className="text-sm px-4 border border-[#1C1C1A]" data-testid="apply-code-button">Apply</button>
                                    ) : (
                                        <button type="button" onClick={removeCode} className="text-sm px-4 border border-[#C7C0B3] text-[#737562]" data-testid="remove-code-button-cta">Remove</button>
                                    )}
                                </div>
                                {codeError && (
                                    <p className="mt-2 text-xs text-red-700" data-testid="discount-code-error">{codeError}</p>
                                )}
                            </div>
                        )}

                        {requiresQuote ? (
                            <div className="mt-6 border border-[#C7C0B3] bg-[#F0EBE1] p-6" data-testid="intl-quote-block">
                                <p className="font-serif text-lg leading-snug text-[#1C1C1A]">
                                    Larger international orders require a personalised shipping quote. Please contact ALATI before payment and we will arrange the best available postage for your order.
                                </p>
                                <Link
                                    to="/contact?subject=International%20Shipping%20Quote"
                                    className="btn-primary mt-6 inline-block"
                                    data-testid="intl-quote-contact"
                                >
                                    Contact ALATI for a Quote
                                </Link>
                            </div>
                        ) : (
                            <>
                                <p className="mt-6 text-sm text-[#4A4A46] leading-relaxed">
                                    {isPickup
                                        ? "You'll be redirected to Stripe to enter your payment details. Your ALATI pickup contact details stay with us."
                                        : "You'll be redirected to Stripe to enter your full delivery address and payment details. We never see your card number."}
                                </p>
                                <button
                                    onClick={goToStripe}
                                    disabled={!canContinue}
                                    className="btn-primary w-full mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="continue-to-stripe-button"
                                >
                                    {busy ? "Redirecting…" : `Continue · ${fmt(preview.total)}`}
                                </button>
                                {isPickup && !pickupComplete && (
                                    <p className="mt-2 text-[11px] text-[#737562] text-center" data-testid="pickup-incomplete-note">
                                        Please complete all pickup contact fields to continue.
                                    </p>
                                )}
                                {!isPickup && !shipComplete && (
                                    <p className="mt-2 text-[11px] text-[#737562] text-center" data-testid="ship-incomplete-note">
                                        Please complete all delivery details to continue.
                                    </p>
                                )}
                                <p className="mt-4 text-[11px] text-[#737562] text-center">
                                    By continuing you agree to ALATI&rsquo;s <Link to="/shipping-returns" className="alati-link">Shipping &amp; Returns</Link> policy.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
